import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"

export default class extends Controller {
  static targets = ["searchInput", "typeSelect", "onlyCompanyCheckbox", "includeArchivedCheckbox", "searchResults", "relationsSection", "relationsContent", "companyCheckboxWrapper"]
  static values = {
    recordType: String,
    recordId: String,
    companyId: String,
  }

  connect() {
    this.searchTimeout = null
    this.relationsData = {}
    this.totalSize = 0

    // Load initial relations data
    this.getRelations()
    // Perform initial search with empty query and company filter
    this.performRelationsSearch()

    // Autofocus on the search input when sidepanel opens
    this.searchInputTarget.focus()

    // Update checkbox visibility on initial load
    this.updateCompanyCheckboxVisibility()
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  updateSearchType() {
    this.updateCompanyCheckboxVisibility()
    this.performRelationsSearch()
  }

  updateCompanyCheckboxVisibility() {
    if (!this.hasCompanyCheckboxWrapperTarget) return

    const selectedType = this.typeSelectTarget.value

    // Hide the checkbox when Company is selected
    this.companyCheckboxWrapperTarget.classList.toggle('hidden', selectedType === 'Company')
  }

  performRelationsSearch() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      const query = this.searchInputTarget.value.trim()

      const params = new URLSearchParams({
        q: query,
        is_relations_search: true,
        type: this.typeSelectTarget.value,
        company: this.hasOnlyCompanyCheckboxTarget && this.onlyCompanyCheckboxTarget.checked ? this.companyIdValue : '',
        include_archived: this.hasIncludeArchivedCheckboxTarget && this.includeArchivedCheckboxTarget.checked,
        disable_type: this.recordTypeValue,
        disable_id: this.recordIdValue
      })

      fetch(`/searches?${params}`)
        .then(response => response.json())
        .then(results => {
          this.displaySearchResults(results)
        })
        .catch(error => {
          console.error('Search error:', error)
          this.searchResultsTarget.innerHTML = '<p>Search failed. Please try again.</p>'
        })
    }, 300)
  }

  getItemDescription(result) {
    let itemDescription = '';

    if (result.asset_type) {
      switch (result.asset_type) {
        case 'Password':
          itemDescription = `${result.company_name}`;

          if (result.folder) {
            itemDescription += ` / ${result.folder}`;
          }

          break;
        case 'Website':
          itemDescription = `${result.company_name}`;
          break;
        default:
          itemDescription = `${result.asset_type} / ${result.company_name}`;
      }
    }

    if (result.object_type === "Article") {
      itemDescription = result.company_name ? `${result.company_name}` : 'Central KB';

      if (result.folder) {
        itemDescription += ` / ${result.folder}`;
      }
    }

    return itemDescription;
  }

  displaySearchResults(results) {
    if (!results || results.length === 0) {
      this.searchResultsTarget.innerHTML = '<p>No results found.</p>'
      return
    }

    const html = results.map(result => `
      <div class="result-item">
        <div class="item-content">
          <a href="${result.url}" target="_blank" class="item-title">
            ${result.name}
          </a>
          <p class="item-description">${this.getItemDescription(result)}</p>
        </div>
        <button class="button button--icon button--icon--small button--secondary button--unfilled"
                data-action="click->relations-sidepanel#addRelation"
                data-toable-id="${result.id}"
                data-toable-type="${result.object_type}">
          <i class="fas fa-plus"></i>
        </button>
      </div>
    `).join('')

    this.searchResultsTarget.innerHTML = html
  }

  addRelation(event) {
    const toableId = event.currentTarget.dataset.toableId
    const toableType = event.currentTarget.dataset.toableType

    fetch('/relations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        relation: {
          fromable_type: this.recordTypeValue,
          fromable_id: this.recordIdValue,
          toable_type: toableType,
          toable_id: toableId
        }
      })
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        return response.json().then(errorData => {
          throw new Error(errorData.error || 'Failed to add relation')
        })
      }
    })
    .then(data => {
      this.getRelations()
      this.performRelationsSearch()
      this.notifyRelationsChanged()
      flash("Relation Added!", "success")
    })
    .catch(error => {
      console.error('Error adding relation:', error)
      flash(error.message || "Something went wrong!", "error")
    })
  }

  removeRelation(event) {
    const relationId = event.currentTarget.dataset.relationId

    if (confirm('Are you sure you want to delete this relation?')) {
      fetch(`/relations/${relationId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      .then(response => {
        if (response.ok) {
          // Refresh the relations data from server to get proper grouping
          this.getRelations()
          // Refresh search results to reflect the change
          this.performRelationsSearch()
          // Notify other components that relations have changed
          this.notifyRelationsChanged()
          flash("Relation removed!", "success")
        } else {
          throw new Error('Failed to remove relation')
        }
      })
      .catch(error => {
        console.error('Error removing relation:', error)
        flash("Something went wrong!", "error")
      })
    }
  }

  getRelations() {
    // Use the same endpoint as the Vue component, with from_sidebar param to include passwords
    fetch(`/relations/get_relations?id=${this.recordIdValue}&type=${this.recordTypeValue}&from_sidebar=true`)
      .then(response => response.json())
      .then(data => {
        // Update our state
        this.relationsData = data.relations
        this.totalSize = data.total_size
        this.renderRelations()
      })
      .catch(error => {
        console.error('Error getting relations:', error)
        flash("Something went wrong!", "error")
      })
  }

    renderRelations() {
    if (!this.hasRelationsContentTarget) return

    let html = ''

    // Calculate current total from actual data
    const currentTotal = Object.values(this.relationsData).reduce((sum, group) => {
      return sum + (group.items ? group.items.length : 0)
    }, 0)

    if (currentTotal === 0) {
      html += `
        <div class="results-group">
          <p id ="empty-relations-message">None</p>
        </div>
      `
    } else {
      // Group relations by type
      Object.keys(this.relationsData).forEach(key => {
        const relationGroup = this.relationsData[key]

        if (relationGroup.items && relationGroup.items.length > 0) {
          html += `
            <div class="results-group">
              <div class="group-header">
                <i class="${relationGroup.icon}"></i>
                ${relationGroup.name} (${relationGroup.items.length})
              </div>
          `

          relationGroup.items.forEach(relation => {
            // Handle different URL formats - initial data vs newly added data
            let url;
            if (relation.toable_url) {
              url = relation.toable_url;
            } else if (relation.url) {
              url = relation.url;
            } else if (relation.toable_id && relation.toable_type) {
              url = this.generateUrlForRelation(relation);
            } else {
              url = '#';
              console.warn('Unable to determine URL for relation:', relation);
            }

            html += `
              <div class="result-item">
                <div class="item-content">
                  <a href="${url}" target="_blank" class="item-title">
                    ${relation.name}
                  </a>
                  ${relation.description ? `<p class="item-description">${relation.description}</p>` : ''}
                </div>
                ${!relationGroup.linked ? `
                  <button class="delete-button"
                          data-action="click->relations-sidepanel#removeRelation"
                          data-relation-id="${relation.id}">
                    <i class="fas fa-trash"></i>
                  </button>
                ` : ''}
              </div>
            `
          })

          html += '</div>'
        }
      })
    }

    this.relationsContentTarget.innerHTML = html
  }

  notifyRelationsChanged() {
    // Dispatch a custom event that the Vue component can listen for
    const event = new CustomEvent('relations:updated', {
      detail: {
        recordType: this.recordTypeValue,
        recordId: this.recordIdValue
      }
    })
    window.dispatchEvent(event)
  }

  generateUrlForRelation(relation) {
    // Generate URL based on relation type
    const type = relation.toable_type.toLowerCase()
    const id = relation.toable_id

    switch(type) {
      case 'asset':
        return `/a/${id}`
      case 'article':
        return `/kba/${id}`
      case 'assetpassword':
        return `/passwords/${id}`
      case 'website':
        return `/websites/${id}`
      case 'network':
        return `/networks/${id}`
      case 'vlan':
        return `/vlans/${id}`
      default:
        return `/${type}s/${id}`
    }
  }
}
